// DropdownMenu.js (Main Component)
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getAuth, signOut, onAuthStateChanged } from 'firebase/auth';
import { useTranslation } from '../../services/useTranslation';

import ConfirmationModal from '../ui/ConfirmationModal';
import DesktopNavigation from './DesktopNavigation';
import MobileNavigation from './MobileNavigation';
import MobileBannerRenderer from './MobileBannerRenderer';

const DropdownMenu = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Determine if we should start fully collapsed based on the route
  // Only show expanded sidebar on the /chat route
  const isChatPage = location.pathname === '/chat';
  const [isFullyCollapsed, setIsFullyCollapsed] = useState(!isChatPage);

  const auth = getAuth();
  const dropdownRef = useRef(null);
  const { t } = useTranslation();

  const hasActiveChat = sessionStorage.getItem('chatState') !== null;
  const showBackToChat = location.pathname !== '/' && hasActiveChat;

  const handleLogout = async () => {
    try {
      sessionStorage.removeItem('chatState');
      await signOut(auth);
      setShowUserMenu(false);
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const openLogoutModal = () => setShowLogoutModal(true);
  const closeLogoutModal = () => setShowLogoutModal(false);

  // Expose the mobile menu toggle function to the window object
  // so it can be accessed from MobileTopBanner
  useEffect(() => {
    window.toggleMobileMenu = () => setShowMobileMenu(prev => !prev);
    
    return () => {
      delete window.toggleMobileMenu;
    };
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) navigate('/login');
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  // Update isFullyCollapsed state when route changes
  useEffect(() => {
    // Only show expanded sidebar on the /chat route
    const isChatPage = location.pathname === '/chat';
    setIsFullyCollapsed(!isChatPage);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const sharedProps = {
    user,
    showBackToChat,
    openLogoutModal,
    t
  };

  return (
    <>
      {/* Mobile Banner - Renders appropriate banner based on current page */}
      <MobileBannerRenderer user={user} />

      <DesktopNavigation
        {...sharedProps}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        isFullyCollapsed={isFullyCollapsed}
        setIsFullyCollapsed={setIsFullyCollapsed}
        showUserMenu={showUserMenu}
        setShowUserMenu={setShowUserMenu}
        dropdownRef={dropdownRef}
      />

      <MobileNavigation
        {...sharedProps}
        showMobileMenu={showMobileMenu}
        setShowMobileMenu={setShowMobileMenu}
      />

      <ConfirmationModal
        isOpen={showLogoutModal}
        onClose={closeLogoutModal}
        onConfirm={handleLogout}
        message={t('navigation.confirmLogout')}
      />
    </>
  );
};

export default DropdownMenu;
